import { ICONS } from '@frontend/icons';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { DropdownItem } from '../dropdown/dropdown-item/dropdown-item.component';
import { Dropdown } from '../dropdown/dropdown.component';
import useSearchWithFilterInput from './search-with-filter-input.controller';
import { SearchFilterOption, SearchWithFilterInputProps } from './search-with-filter-input.types';

const ID = 'search-with-filter-input';

export const SearchWithFilterInput = (props: SearchWithFilterInputProps) => {
    const viewProps = useSearchWithFilterInput(props);

    function getErrorMessage(): React.ReactNode {
        if (props.required) {
            return (
                <FormattedMessage
                    id='search-with-filter-input.required'
                    defaultMessage='{label} is required'
                    values={{ label: props.label }}
                />
            );
        }
        return (
            <FormattedMessage
                id='search-with-filter-input.error'
                defaultMessage='An error occurred with field {label}'
                values={{ label: props.label }}
            />
        );
    }

    const handleFilterSelect = (option: SearchFilterOption) => {
        viewProps.setSelectedFilter(option);
        props.onFilterChange?.(option.key);
        // Re-trigger search with new filter if there's a value
        if (viewProps.filterValue) {
            props.onChange?.(viewProps.filterValue, option.key);
        }
    };

    return (
        <div
            id={props.id ?? ID}
            className={'inline-flex h-full w-full flex-col items-start justify-start gap-1 self-stretch py-0.5 ' + (props.className ?? '')}>
            {props.label && (
                <label className='inline-flex flex-row items-center justify-start gap-1 self-stretch'>
                    {props.label && props.required ? <span className='text-danger me-1 text-[8px]'>&#9679;</span> : <></>}
                    {props.label}
                    {props.helpMessage && (
                        <>
                            <br />
                            <small className='form-text text-brand-muted'>{props.helpMessage}</small>
                        </>
                    )}
                </label>
            )}
            <div className='outline-border-default bg-background-surface flex h-full w-full items-center justify-between rounded-xl px-4 outline'>
                <div className='flex items-center gap-2'>
                    <ICONS.BUTTON.SEARCH className='text-brand-muted h-5 w-5' />
                    <input
                        className='form-control placeholder:text-border-default bg-background-surface border-0 text-base font-medium focus:outline-none'
                        autoFocus={props.autoFocus}
                        id={props.id ? `${props.id}-input` : undefined}
                        data-testid={props.id ? `${props.id}-input` : undefined}
                        value={viewProps.filterValue ?? undefined}
                        type='text'
                        placeholder={props.placeholder}
                        onFocus={() => {
                            viewProps.changeTouched(true);
                            props.onFocusChange && props.onFocusChange();
                        }}
                        required={props.required}
                        onChange={(event) => {
                            if (props.onChange) viewProps.setFilterValue(event.target.value);
                            viewProps.isValid();
                        }}
                        disabled={props.disabled}
                    />
                </div>
                {props.filterOptions && props.filterOptions.length > 0 && (
                    <Dropdown
                        hideChevron={false}
                        label={<span className='text-brand-muted text-base font-medium'>{viewProps.selectedFilter?.label}</span>}
                        buttonStyle='flex items-center gap-2 cursor-pointer'
                        styles={{
                            label: { text: 'text-brand-muted' }
                        }}>
                        {props.filterOptions.map((option) => (
                            <DropdownItem
                                key={option.key}
                                label={option.label}
                                onClick={() => handleFilterSelect(option)}
                            />
                        ))}
                    </Dropdown>
                )}
            </div>

            {viewProps.valid === false && viewProps.touched === true ? (
                <span className='badge bg-danger mt-2'>
                    <small>{props.errorMessage ?? getErrorMessage()}</small>
                </span>
            ) : (
                <></>
            )}
        </div>
    );
};
